<template lang="pug">
  input(
    :value="value"
    @input="$emit('update:value', $event.target.value)"
    :type="inputType"
    class="dynamic-number-input"
    :class="numberInputClasses"
    :style="numberInputStyles"
    :placeholder="placeholder"
    :disabled="disableNumberInput"
    v-bind="additionalAttrs"
    @focus="onFocus"
    @blur="$emit('blur', value)"
    @keydown="onKeyDown"
    @beforeinput="onBeforeInput"
  )
</template>

<script>
import responsive from '~/mixins/pages/responsive'

export default {
  name: 'TemplatesDynamicNumeric',
  mixins: [responsive],
  props: {
    field: {
      type: Object,
      default: null
    },
    value: {
      type: String,
      default: null
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    fontSize: {
      type: [String, Number],
      default: null
    },
    isResizing: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: null
    }
  },
  computed: {
    inputType() {
      return this.canEdit || this.xsDevice ? 'text' : 'number'
    },
    disableNumberInput() {
      return (!this.field.documentFieldValue && this.field.documentFieldPreValue) || this.isDisabled
    },
    numberInputStyles() {
      return {
        fontSize: this.xsDevice ? '7px' : this.fontSize + 'px'
      }
    },
    numberInputClasses() {
      return {
        filled: this.value && !this.canEdit,
        'error-state': this.hasError
      }
    },
    additionalAttrs() {
      const additionalAttrs = {}
      if (this.xsDevice) {
        additionalAttrs.inputmode = 'decimal'
      }
      return additionalAttrs
    }
  },
  mounted() {
    this.setNumber()
  },
  methods: {
    onFocus() {
      this.$store.commit('templatefields/SET_ACTIVE_FIELD_ID', this.field.id)
      this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
        id: this.field.id,
        value: true
      })
    },
    onKeyDown(e) {
      if (!this.canEdit && e.key === '+') {
        e.preventDefault()
        return
      }
      if (e.ctrlKey && e.key === 'c') {
        this.$emit('copy')
      }
      if (e.ctrlKey && e.key === 'x') {
        this.$emit('cut')
      }
      if (e.key === 'Delete') {
        this.$emit('delete')
      }
    },
    setNumber() {
      if (this.canEdit) {
        this.$emit('update:value', this.field.hint)
      } else if (this.field.documentFieldPreValue) {
        this.$emit('update:value', this.field.documentFieldPreValue)
      } else {
        this.$emit('update:value', this.field.documentFieldValue)
      }
    },
    onBeforeInput(e) {
      const val = e.target.value
      const inputChar = e.data

      if ((inputChar === '.' || inputChar === ',') && /[.,]/.test(val)) {
        e.preventDefault()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.dynamic-number-input {
  width: 100%;
  height: 100%;

  border-radius: 8px;
  border: 1px solid $primary;
  background: rgba(255, 255, 255, 0.7);
  padding: 6px;
  resize: none;
  outline: none;
  font-weight: 300;

  @media (max-width: map-get($breakpoints, 'sm')) {
    padding: 3px;
    line-height: 1.3;
    text-align: center;
    border-radius: 4px;
    position: relative;
    top: -8px;
  }

  &.filled {
    background:
      linear-gradient(0deg, rgba(143, 37, 125, 0.12) 0%, rgba(143, 37, 125, 0.12)) 100%,
      rgba(255, 255, 255, 0.4);
    color: $primary;
  }

  &.error-state {
    border: 1px solid $danger;
    background: rgba(255, 23, 68, 0.12);
  }

  &::placeholder {
    color: $text-dark;
  }

  /* hiding arrows */
  -moz-appearance: textfield;
  appearance: none;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
